import React, { useState, useEffect, useCallback } from 'react'
import { Plus, PackageOpen, Undo2, Search } from 'lucide-react'
import { FeaturedLayout } from '../../layouts'
import {
  Button, Input, Card, CardContent, Badge,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '../../ui'
import {
  getPrestamosActivos, devolverPrestamo, getAllStaffIncludingInactive, getAllPlayersWithGroups,
} from '../../../lib/supabase'
import { useConfirmDialog } from '../../../hooks/useConfirmDialog'
import PrestamoFormModal from './PrestamoFormModal'

const TIPO_LABELS = {
  prestamo: 'Préstamo',
  asignacion: 'Asignación',
}

const PRESTADO_A_LABELS = {
  staff: 'Staff',
  jugador: 'Jugador',
  externo: 'Externo',
}

const InventarioPrestamos = () => {
  const { confirm, alert, ConfirmDialog } = useConfirmDialog()
  const [loans, setLoans] = useState([])
  const [search, setSearch] = useState('')
  const [staffById, setStaffById] = useState({})
  const [playersById, setPlayersById] = useState({})
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    // El nombre de quien YA tiene un prestamo: tambien de quien se dio de baja en el club (getAllStaff solo trae membresias
    // activas y su prestamo saldria "Sin asignar"). La lista para un prestamo nuevo es la de PrestamoFormModal (getAllStaff).
    const [loansRes, staffRes, playersRes] = await Promise.all([
      getPrestamosActivos(),
      getAllStaffIncludingInactive(),
      getAllPlayersWithGroups(),
    ])
    setLoans(loansRes.data || [])
    setStaffById(
      Object.fromEntries((staffRes.data || []).map((s) => [s.id, s.nombre_completo]))
    )
    setPlayersById(
      Object.fromEntries((playersRes.data || []).map((p) => [p.id, p.nombre_completo]))
    )
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const resolvePrestatario = (loan) => {
    if (loan.prestado_a_tipo === 'externo') return loan.prestado_a_nombre || 'Externo'
    const map = loan.prestado_a_tipo === 'staff' ? staffById : playersById
    return map[loan.prestado_a_id] || 'Sin asignar'
  }

  const handleDevolver = async (loan) => {
    const nombre = loan.item?.nombre || 'el artículo'
    const ok = await confirm(
      'Registrar devolución',
      `¿Marcar como devuelto "${nombre}" (${loan.cantidad}) de ${resolvePrestatario(loan)}?`,
      { confirmText: 'Devolver' }
    )
    if (!ok) return
    const { error } = await devolverPrestamo(loan.id)
    if (error) {
      await alert('Error', error.message || 'No se pudo registrar la devolución')
      return
    }
    load()
  }

  const term = search.trim().toLowerCase()
  const loansFiltrados = term
    ? loans.filter((l) =>
        [l.item?.nombre, resolvePrestatario(l), l.notas]
          .some((v) => (v || '').toString().toLowerCase().includes(term))
      )
    : loans

  return (
    <FeaturedLayout
      title="Préstamos"
      subtitle="Artículos prestados o asignados pendientes de devolución"
      refreshAction={load}
    >
      <div className="mb-4 flex items-center gap-2 flex-nowrap">
        <div className="flex-1 min-w-0 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar artículo o persona..."
            className="pl-10"
          />
        </div>
        <span className="text-sm text-muted-foreground hidden sm:block whitespace-nowrap">
          <span className="font-medium text-foreground">{loansFiltrados.length}</span> activos
        </span>
        <Button onClick={() => setFormOpen(true)} className="h-10 shrink-0" aria-label="Nuevo préstamo" title="Nuevo préstamo">
          <Plus className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Nuevo préstamo</span>
        </Button>
      </div>

      {loading ? (
        <p className="text-muted-foreground text-sm py-8 text-center">Cargando...</p>
      ) : loans.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <PackageOpen className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
            <p className="text-muted-foreground mb-4">No hay préstamos activos</p>
            <Button onClick={() => setFormOpen(true)}>
              <Plus className="h-4 w-4 mr-2" /> Registrar préstamo
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Artículo</TableHead>
                <TableHead className="text-right">Cantidad</TableHead>
                <TableHead className="hidden sm:table-cell">Tipo</TableHead>
                <TableHead>Prestado a</TableHead>
                <TableHead className="hidden md:table-cell">Fecha</TableHead>
                <TableHead className="w-28 text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loansFiltrados.map((loan) => {
                const detalle = [loan.item?.talla, loan.item?.modelo].filter(Boolean).join(' · ')
                return (
                  <TableRow key={loan.id}>
                    <TableCell>
                      <div>
                        <span className="font-medium">{loan.item?.nombre || 'Artículo'}</span>
                        {detalle && <p className="text-xs text-muted-foreground">{detalle}</p>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{loan.cantidad}</TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <Badge variant="secondary" className="text-xs">
                        {TIPO_LABELS[loan.tipo] || loan.tipo}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div>
                        <span className="text-sm">{resolvePrestatario(loan)}</span>
                        <p className="text-xs text-muted-foreground">
                          {PRESTADO_A_LABELS[loan.prestado_a_tipo] || loan.prestado_a_tipo}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                      {loan.fecha_prestamo || '—'}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end">
                        <Button variant="outline" size="sm" onClick={() => handleDevolver(loan)}>
                          <Undo2 className="h-3.5 w-3.5 sm:mr-1" />
                          <span className="hidden sm:inline">Devolver</span>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      <PrestamoFormModal
        open={formOpen}
        onOpenChange={setFormOpen}
        onSuccess={load}
      />

      <ConfirmDialog />
    </FeaturedLayout>
  )
}

export default InventarioPrestamos
